import { useState, useEffect, useId, useRef } from 'react'

import { UseFormReturn } from 'react-hook-form'

import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { RadioGroup } from '@tribeplatform/react-ui-kit/RadioGroup'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { Form } from '../../Form/index.js'
import { useI18n, T } from '../../i18n/index.js'

import { usePollFormContext } from './PollFormContext.js'
import { PollFormValues } from './types.js'

type SelectionType = 'single' | 'multiple'

/**
 * Form.Input always returns string values, so maxSelections is handled as a string
 * to avoid breaking changes in Form.Input that could affect other components.
 */
export function PollFormSelectionType({
  form,
}: {
  form: UseFormReturn<PollFormValues>
}) {
  const id = useId()
  const { $t } = useI18n()
  const { hasAlreadyVoted } = usePollFormContext()
  const defaultMaxSelections = useRef(form.getValues('maxSelections'))

  const maxOptions = form.watch('options').length

  const [multiSelect, setMultiSelect] = useState<SelectionType>(() => {
    if (Number(form.getValues('maxSelections')) > 1) {
      return 'multiple'
    }
    return 'single'
  })

  useEffect(() => {
    switch (multiSelect) {
      case 'multiple':
        if (Number(defaultMaxSelections.current) > 1) {
          form.resetField('maxSelections')
        } else {
          form.setValue('maxSelections', '2', { shouldDirty: true })
        }
        break
      case 'single':
        if (Number(defaultMaxSelections.current) > 1) {
          form.setValue('maxSelections', '1', { shouldDirty: true })
        } else {
          form.resetField('maxSelections')
        }
        break
      default:
        throw new Error('Invalid selection type')
    }
  }, [multiSelect, form])

  return (
    <div className="space-y-4">
      <FormControl.Label htmlFor={`poll-selection-type-${id}`}>
        <div className="flex flex-col">
          <Text variant="labelLg">
            <T
              id="Poll.Form.MaxSelections.Title"
              defaultMessage="Selection limit"
            />
          </Text>
          <Text className="text-content-muted" variant="bodySm">
            <T
              id="Poll.Form.MaxSelections.Description"
              defaultMessage="Choose whether voters can select only one or multiple choices."
            />
          </Text>
        </div>
      </FormControl.Label>
      <FormControl.RadioGroup
        id={`poll-selection-type-${id}`}
        name="multipleSelect"
        value={multiSelect}
        onChange={value => {
          setMultiSelect(value as 'single' | 'multiple')
        }}
        disabled={hasAlreadyVoted}
      >
        <RadioGroup.Items>
          <RadioGroup.Item
            value="single"
            title={$t({
              id: 'Poll.Form.MaxSelections.SingleChoice',
              defaultMessage: 'Single choice',
            })}
          />
          <RadioGroup.Item
            value="multiple"
            title={$t({
              id: 'Poll.Form.MaxSelections.MultipleChoices',
              defaultMessage: 'Multiple choices',
            })}
          />
        </RadioGroup.Items>
      </FormControl.RadioGroup>
      {multiSelect === 'multiple' && (
        <div className="flex flex-col gap-2 ps-7">
          <FormControl.Label htmlFor={`max-selections-${id}`}>
            <T
              id="Poll.Form.MaxSelections"
              defaultMessage="Maximum selections allowed"
            />
          </FormControl.Label>
          <Form.Input
            id={`max-selections-${id}`}
            name="maxSelections"
            type="number"
            min={2}
            max={maxOptions}
            disabled={hasAlreadyVoted}
          />
        </div>
      )}
      {hasAlreadyVoted && (
        <Alert status="info">
          <T
            id="Poll.Form.MaxSelections.LiveNotAllowed"
            defaultMessage="Some users have already participated in this poll. You can no longer change the selection limit."
          />
        </Alert>
      )}
    </div>
  )
}
